import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, FormControl, TextField, Button, Paper } from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AttachIcon from '@mui/icons-material/AttachFile';
import { DatePicker } from '@mui/x-date-pickers';
import { uniqueId } from 'lodash';
import filesize from "filesize";
import { map as asyncMap } from 'p-iteration';
import QuestionsList from '../../Components/QuestionsList';
import ServiceOldActionsPanel, { IOldAction } from '../../../../components/ServiceOldActionsPanel/ServiceOldActionsPanel';
import { EAccessRole, RootState } from '../../../../store/types';
import { i18n } from '../../../../translate/i18n';
import Upload from '../../../../UI/Upload';
import FileList, { IFile } from '../../../../UI/FileList';
import confirm from '../../../../UI/Confirm/confirm';
import api from '../../../../axios/api';
import processUpload from '../../../../utils/processUpload';
import resizeFile from '../../../../utils/imageResizer';
import * as actions from '../../../../store/actions';
import Signature from './Signature';
import { IAction } from '../../../../models/iaction';
import Spinner from '../../../../components/Spinner';
import EquipmentCard from '../EquipmentServicesList/EquipmentCard';
import { IQuestion } from '../../../../models/iquestion';
import DraftList from './DraftList';
import ModalServiceDrafts from './ModalServiceDrafts';
import EFeatureFlags from '../../../../utils/EFeatureFlags';

type IQuestionEdited = {
  _id: string,
  questionsId: string,
  name: string,
  groupName: string,
  type: string,
  answer: string,
  actions: IAction[],
  comment: string,
  commentFlag: boolean,
  fileFlag: boolean,
  files: string[],
  weight: number,
  questionHadPendingActions: boolean,
  description?: string,
  photo?: string,
}

export interface ICreateServiceBody {
  date: Date,
  place: string,
  equipment: string,
  description: string,
  equipmentService: string,
  questions: IQuestionEdited[],
  files: string[],
  source: "web",
  latitude: string,
  longitude: string,
  serviceTime:{
    start: Date,
    end: Date,
  }
  mandatoryActionsForNotComplyQuestions: boolean,
  signatures: {
    name: string,
    complement: string,
    file: string | null,
  }[],
}

export enum EUpdateDraftType {
  questions = 'questions',
  signatures = 'signatures',
  date = 'date',
  description = 'description',
  actions = 'actions',
}

export type IUpdateServiceDraftBody = {
  type: EUpdateDraftType,
  date?: string,
  description?: string,
  files?: string[],
  question?: IQuestionEdited,
  signatures?: {
    name: string,
    complement: string,
    file: string | null,
  }[],
  action?: IAction,
};

type Props = {
  goBack: CallableFunction,
}

const Checklist: React.FC<Props> = ({ goBack }) => {
  const dispatch = useDispatch();
  const [description, setDescription] = useState<string>("");
  const [document, setDocument] = useState<string>("");
  const [accountable, setAccountable] = useState<string>("");
  const [uploadSubmitError, setUploadSubmitError] = useState<boolean>(false);
  const [fileUploadError, setFileUploadError] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<Date>();
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");
  const [openDraftsModal, setOpenDraftsModal] = useState<boolean>(false);

  const questionTree = useSelector((state: RootState) => state.createService.questionsTree);
  const questionsLoading = useSelector((state: RootState) => state.createService.questionsLoading);
  const oldActions = useSelector((state: RootState) => state.createService.oldActions);
  const questions = useSelector((state: RootState) => state.createService.questions);
  const uploadedFiles = useSelector((state: RootState) => state.createService.files);
  const totalQuestions = useSelector((state: RootState) => state.createService.totalQuestions);
  const answeredQuestions = useSelector((state: RootState) => state.createService.answeredQuestions);
  const numberOfQuestionsThatShouldHaveActions = useSelector((state: RootState) => state.createService.numberOfQuestionsThatShouldHaveAction);
  const tenant = useSelector((state: any) => state.auth.selectedAccess.tenant);
  const signature = useSelector((state: RootState) => state.createService.signature);
  const dateServiceForm = useSelector((state: RootState) => state.createService.date);
  const equipment = useSelector((state:RootState) => state.createService.selectedEquipmentWithServices);
  const equipmentId = useSelector((state:RootState) => state.createService.selectedEquipmentId);
  const equipmentService = useSelector((state:RootState) => state.createService.selectedEquipmentService);
  const siteId = useSelector((state: RootState) => state.createService.selectedEquipmentWithServices.site._id);
  const postServiceLoading = useSelector((state:RootState) => state.createService.postServiceLoading);
  const equipmentLoading = useSelector((state:RootState) => state.createService.equipmentsLoading);
  const selectedAccess = useSelector((state: any) => state.auth.selectedAccess);
  const checklistScore = useSelector((state: RootState) => state.createService.checklistScore);
  const selectedEquipmentWithServices = useSelector((state:RootState) => state.createService.selectedEquipmentWithServices);
  const signatures = useSelector((state: RootState) => state.createService.signatures);
  const defaultAnswerNA = selectedAccess.tenant.featureFlags.includes('NA_AS_DEFAULT_ANSWER');
  const serviceDraftId = useSelector((state: RootState) => state.createService.serviceDraftId);
  const serviceDrafts = useSelector((state: RootState) => state.createService.serviceDrafts);
  const draftDate = useSelector((state: RootState) => state.createService.draftDate);
  const draftDescription = useSelector((state: RootState) => state.createService.draftDescription);
  const createServiceDraftLoading = useSelector((state: RootState) => state.createService.createServiceDraftLoading);
  const role = useSelector((state: any) => state.auth.selectedAccess.role);
  const isAnalyst = role === EAccessRole.analyst;
  const isContractor = role === EAccessRole.contractor;
  const disableRetroactiveServiceForAnalystsAndContractors = selectedAccess.tenant.featureFlags.includes(EFeatureFlags.BLOCK_RETROACTIVE_SERVICES_FOR_ANALYSTS_AND_CONTRACTORS);
  const disableRetroactiveService = disableRetroactiveServiceForAnalystsAndContractors && (isAnalyst || isContractor);

  const equipmentServiceId = equipmentService._id;

  const postService = (body: ICreateServiceBody): any => dispatch(actions.createServicePostService(body));
  const loadDraft = async (id: string): Promise<any> => dispatch(actions.createServiceLoadDraft(id));
  const deleteDraft = (id: string): any => dispatch(actions.createServiceDeleteDraft(id, 1, 100, equipmentServiceId, equipmentId));

  const oldActionsWithoutQuestion: IOldAction[] = [];
  oldActions.forEach((action) => {
    const thereIsAQuestionForThisAction = questions.some((question) => {
      const questionId = question._id || question.questionsId;
      return questionId === action.question;
    });
    if (!thereIsAQuestionForThisAction) oldActionsWithoutQuestion.push(action);
  });

  const questionsRef:any[] = [];

  useEffect(() => {
    setStartTime(new Date())
    const location = window.navigator?.geolocation;
    location.getCurrentPosition((position) => {
      setLatitude(position.coords.latitude.toString())
      setLongitude(position.coords.longitude.toString())
    }, (error) => {
      if (error.message === 'User denied Geolocation') {
        setLatitude('no-authorization')
        setLongitude('no-authorization')
      }
    })
  }, []);

  useEffect(() => {
    if (equipmentId && equipmentServiceId && siteId) {
      dispatch(actions.createServicefetchOldActions(equipmentId, equipmentServiceId, siteId, '', defaultAnswerNA, serviceDraftId));
      dispatch(actions.createServicefetchServiceDrafts(1, 100, equipmentId, equipmentServiceId));
    }
  }, [equipmentId, equipmentServiceId, siteId]);
  
  useEffect(() => {
    setDescription(draftDescription);
  }, [draftDescription]);

  const handleUpload = async (files: File[]) => {
    const uploadingFiles = await asyncMap(files, async (file) => {
      const fileOptimized = await resizeFile(file)

      return ({
        file: fileOptimized,
        id: uniqueId(),
        name: fileOptimized.name,
        readableSize: filesize(fileOptimized.size),
        preview: URL.createObjectURL(fileOptimized),
        fileType: fileOptimized.type,
        progress: 0,
        isNotUploaded: true,
        error: false,
        url: null
      } as IFile);
    });

    setUploadSubmitError(false);
    setFileUploadError(false);

    dispatch(actions.createServiceAddObservationFile(uploadingFiles));
    const processUploadPromises = uploadingFiles.map(async (file) => await processUpload(file, updateFile, setUploadSubmitError));
    return await Promise.all(processUploadPromises);
  };

  const updateFile = (fileId: string, file: any) => {
    dispatch(actions.createServiceEditObservationFile(file, fileId));
  };

  const retryUpload = async (file: IFile) => {
    updateFile(file.id as string, {error: false})
    await processUpload(file, updateFile, setUploadSubmitError);
  }

  const handleDeleteServiceFile = (fileId: string) => {
    confirm(i18n.t('handleDeleteDocumentFileFormService.confirm'),
      {
        title: i18n.t('handleDeleteDocumentFileFormService.title'),
        titleColor: 'red',
        disableBackdrop: true
      }).then(
        () => { },
        async () => {
          await handleDeleteFile(fileId)
        },
      )
  }
  
  const handleDeleteFile = async (fileId: string) => {
    try {
      setUploadSubmitError(false);
      setFileUploadError(false);
      if (fileId.length === 24) await api.delete(`files/${fileId}`)
      dispatch(actions.createServiceDeleteObservationFile(fileId));
    } catch (e) {
      console.log('e', e);
    }
  }

  const getValidUnansweredQuestionsRefs = () => questionsRef.filter(ref => ref.current !== null);

  const scrollToFirstUnansweredQuestion = () => {
    const validUnansweredQuestionsRefs = getValidUnansweredQuestionsRefs();    
    return validUnansweredQuestionsRefs[0]!.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const needsSignatures = (equipmentService.mandatorySignature && signatures.length === 0);

  const handleCreateServiceDraft = async (data: IUpdateServiceDraftBody) => {
    let hasError = false;
    let filesIds: string[] = [];
    let signaturesFormatted: {name: string, complement: string, file: string}[] = [];

    if (data.files && data.files.length > 0) {
      filesIds = data.files;
    }

    if (data.signatures && data.signatures.length > 0) {
      signaturesFormatted = data.signatures.map((signature) => ({name: "", complement: "", file: signature.file!}));
    }
    
    if (hasError) return;
    let questionsArray: IQuestionEdited[] = []
    questionTree.forEach(group => {
      const groupEdited: IQuestionEdited = {
        _id: group.id,
        questionsId: group.id,
        name: group.name,
        groupName: "",
        type: "group",
        answer: "NA",
        actions: [],
        comment: "",
        commentFlag: false,
        fileFlag: false,
        files: [],
        weight: 1,
        questionHadPendingActions: false,
      }
      questionsArray.push(groupEdited);
      group.questionArray.forEach((question: IQuestion) => {
        const questionFiles = question.files;
        if (questionFiles.some((file) => file.error)) {
          setFileUploadError(true)
          hasError = true;
        } else if (questionFiles.some((file) => file.isNotUploaded)) {
          setUploadSubmitError(true)
          hasError = true;
        }
        const questionEdited = {
          _id: question._id,
          questionsId: question._id,
          name: question.name,
          groupName: question.groupName as string,
          type: question.type,
          answer: question.answer,
          actions: question.actions.map((action) => {
            return {
              ...action,
              files: action.files.map((file: any) => {
                if (typeof file === "string") {
                  return file;
                } else {
                  return (file._id || file.id) as string
                }
              }),
            }
          }),
          comment: question.comment,
          commentFlag: question.commentFlag,
          fileFlag: question.fileFlag,
          files: questionFiles.map((file) => file.id as string),
          weight: question.weight,
          questionHadPendingActions: false,
          description: question.description,
          photo: question.photo?._id,
        }

        questionEdited.questionHadPendingActions = oldActions.some((action) => {
          const questionId = question._id;
          return questionId === action.question;
        }) || question.actions?.length > 0;
        questionsArray.push(questionEdited) 
      })


    })
  
    if (hasError) return;
    
    const body = {
      date: dateServiceForm,
      place: equipment.place._id,
      equipment: equipmentId,
      description,
      equipmentService: equipmentServiceId,
      questions: questionsArray,
      files: filesIds as string[],
      source: "web" as "web",
      latitude,
      longitude,
      serviceTime:{
        start: startTime as Date,
        end: new Date() as Date,
      },
      mandatoryActionsForNotComplyQuestions: equipmentService.mandatoryActionsForNotComplyQuestions,
      equipmentModel: selectedEquipmentWithServices.equipmentModel._id,
      serviceType: equipmentService.serviceType._id,
      frequency: equipmentService.frequency._id,
      signatures: signaturesFormatted,
    }
    dispatch(actions.createServiceDraft(body));
  }

  const handleUpdateServiceDraft = async (body: IUpdateServiceDraftBody) => {
    dispatch(actions.updateServiceDraft(serviceDraftId, body));
  }

  const createOrUpdateDraft = async (body: IUpdateServiceDraftBody) => {
    if (serviceDraftId) {
      await handleUpdateServiceDraft(body);
    } else {
      await handleCreateServiceDraft(body);
    }
  }

  const handleSubmit = () => {
    let hasError = false;

    const notUploaded = [];
    const withError = [];
    uploadedFiles.forEach(file => {
      if (file.error) withError.push(file);
      else if(file.isNotUploaded) notUploaded.push(file);
    })

    if (notUploaded.length > 0) {
      setUploadSubmitError(true)
      hasError = true;
    }

    if (withError.length > 0) {
      setFileUploadError(true)
      hasError = true;
    }

    if (needsSignatures) {
      hasError = true;
    }

    const filesIds = uploadedFiles.map(fl => {const id = fl.id || fl._id; return id});

    const validUnansweredQuestionsRefs = getValidUnansweredQuestionsRefs();
    if (validUnansweredQuestionsRefs.length > 0) {
      return scrollToFirstUnansweredQuestion();
    }
    
    let questionsArray: IQuestionEdited[] = []    
    if (hasError) return;
    questionTree.forEach(group => {
      const groupEdited: IQuestionEdited = {
        _id: group.id,
        questionsId: group.id,
        name: group.name,
        groupName: "",
        type: "group",
        answer: "NA",
        actions: [],
        comment: "",
        commentFlag: false,
        fileFlag: false,
        files: [],
        weight: 1,
        questionHadPendingActions: false,
      }
      questionsArray.push(groupEdited);
      group.questionArray.forEach((question: IQuestion) => {
        const questionFiles = question.files;
        if (questionFiles.some((file) => file.error)) {
          setFileUploadError(true)
          hasError = true;
        } else if (questionFiles.some((file) => file.isNotUploaded)) {
          setUploadSubmitError(true)
          hasError = true;
        }
        const questionEdited = {
          _id: question._id,
          questionsId: question._id,
          name: question.name,
          groupName: question.groupName as string,
          type: question.type,
          answer: question.answer,
          actions: question.actions.map((action) => {
            return {
              ...action,
              // Normalização defensiva: actions regravadas pelo app a partir de um
              // serviceDraft podem perder o campo `question`, que é obrigatório no
              // backend. Caso esteja ausente, herda da questão pai (question._id).
              question: action.question || question._id,
              files: action.files.map((file: any) => {
                if (typeof file === "string") {
                  return file;
                } else {
                  return (file._id || file.id) as string
                }
              }),
            }
          }),
          comment: question.comment,
          commentFlag: question.commentFlag,
          fileFlag: question.fileFlag,
          files: questionFiles.map((file) => (file._id || file.id) as string),
          weight: question.weight,
          questionHadPendingActions: false,
          description: question.description,
          photo: question.photo?._id,
        }

        questionEdited.questionHadPendingActions = oldActions.some((action) => {
          const questionId = question._id;
          return questionId === action.question;
        }) || question.actions?.length > 0;
        questionsArray.push(questionEdited) 
      })


    })
  
    if (hasError) return;
    
    const body = {
      serviceDraftId,
      date: dateServiceForm,
      place: equipment.place._id,
      equipment: equipmentId,
      description,
      equipmentService: equipmentServiceId,
      questions: questionsArray,
      files: filesIds as string[],
      source: "web" as "web",
      latitude,
      longitude,
      serviceTime:{
        start: startTime as Date,
        end: new Date() as Date,
      },
      mandatoryActionsForNotComplyQuestions: equipmentService.mandatoryActionsForNotComplyQuestions,
      equipmentModel: selectedEquipmentWithServices.equipmentModel._id,
      serviceType: equipmentService.serviceType._id,
      frequency: equipmentService.frequency._id,
      signatures: signatures.map((signature: any) => ({
        name: signature.name,
        complement: signature.complement,
        file: signature.file.signatureId || signature.file._id,
      })),
    }

    postService(body).then((result: string) => {
      if (result === 'success') {
        goBack();
      }
    })
  }

  const areThereQuestionsToAnswer = answeredQuestions < totalQuestions;

  const canFinishService = !(areThereQuestionsToAnswer || numberOfQuestionsThatShouldHaveActions > 0 || needsSignatures);

  return (<>
    <ModalServiceDrafts
      isOpen={openDraftsModal}
      onClose={() => setOpenDraftsModal(false)}
      loadDraft={loadDraft}
      deleteDraft={deleteDraft}
    />
    <Box sx={{position: 'relative'}}>
      {(postServiceLoading || questionsLoading || equipmentLoading || createServiceDraftLoading) && <Spinner overlay size={30} />}
      {serviceDrafts.length > 0 && <DraftList openModal={() => setOpenDraftsModal(true)}  />}
      <Paper sx={styles.equipmentCard}>
        <EquipmentCard showEquipmentService />
        <DatePicker
          label={i18n.t('editService.serviceDate') as string}
          value={dateServiceForm}
          disabled={disableRetroactiveService}
          onChange={(date) => {dispatch(actions.createServiceChangeServiceDate(date as Date)); createOrUpdateDraft({type:EUpdateDraftType.date, date: date?.toISOString()});}}
          format="dd/MM/yyyy"
          maxDate={new Date()}
          slotProps={{
            textField: {
              variant: 'standard',
            },
          }}
          sx={styles.datePicker}
        />
      </Paper>
      {!questionsLoading &&
        <QuestionsList
          editMode={false}
          isNewData
          mandatoryActionsForNotComplyQuestions={equipmentService.mandatoryActionsForNotComplyQuestions}
          questionsRefs={questionsRef}
          questionTree={questionTree}
          checklistScore={checklistScore}
          goToActionPage={() => {}}
          createOrUpdateDraft={createOrUpdateDraft}
        />
      }
      { oldActionsWithoutQuestion.length > 0 && 
        <>
          <Box sx={{marginTop: '20px', display: 'flex', marginBottom: '10px'}}>
            <PendingActionsIcon sx={{marginRight: '5px'}} />
            <Typography style={{ color: '#000' }}>
              {i18n.t('serviceOldActionsPanel.title2') as string}
            </Typography>
          </Box>
          <ServiceOldActionsPanel actions={oldActionsWithoutQuestion} showRefreshButton={false}/>
        </>
      }
      <Box style={styles.sectionTitleContainer}>
        <AttachIcon style={{ color: '#000' }} />
        <Typography style={{ color: '#000' }}>{i18n.t('typographyFormService.attachFiles') as string}</Typography>
      </Box>
      <Upload
        onUpload={
          async (files: File[]) => {
            const newFilesIds = await handleUpload(files);
            const filesIds = uploadedFiles.map(fl => (fl.id || fl._id) as string ).concat(newFilesIds);
            await createOrUpdateDraft({type:EUpdateDraftType.description, description, files: filesIds});
          }
        }
      />
      {!!uploadedFiles.length && (
        <>
          <FileList
            files={uploadedFiles}
            onDelete={handleDeleteServiceFile}
            disabled={false}
            retry={(file: IFile) => retryUpload(file)}
          />
          {uploadSubmitError && <Box sx={styles.fileError}>{i18n.t('BoxFormService.fileError') as string}</Box>}
        </>
      )}
      <FormControl variant="standard" fullWidth>
        <TextField
          variant="standard"
          disabled={false}
          label={i18n.t('textFieldFormService.description') as string}
          name='obs'
          onChange={event => setDescription(event.target.value.toUpperCase()) }
          onBlur={event => createOrUpdateDraft({type:EUpdateDraftType.description, description: event.target.value.toUpperCase(), files: uploadedFiles.map(fl => fl.id as string)}) }
          value={description}
          margin='normal' />
      </FormControl>
      {(equipmentService.enableSignature) &&
        <Signature
          isNewData={true}
          isMandatory={equipmentService.mandatorySignature}
          createOrUpdateDraft={createOrUpdateDraft}
        />
      }
      
      <Box sx={styles.submitButtons}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color={canFinishService ? "primary" : "warning" }
          fullWidth={true}
          disabled={questionsLoading || postServiceLoading}
          sx={{
            ...styles.submitButton,
            backgroundColor: canFinishService ? null : '#fcbe03',
            ":hover": canFinishService ? null : { bgcolor: "#ff9800" },
          }}
        >
          {areThereQuestionsToAnswer && <Typography>{i18n.t('checklistServiceButton.scroll') as string}</Typography>}
          {numberOfQuestionsThatShouldHaveActions > 0 && <Typography>{i18n.t('checklistServiceButton.needAction') as string}</Typography>}
          { needsSignatures && <Typography>{i18n.t('checklistServiceButton.needSignatures') as string}</Typography>}
          <Typography>{`${i18n.t('checklistServiceButton.create')} (${answeredQuestions}/${totalQuestions})`}</Typography>
        </Button>
      </Box>
      {uploadSubmitError && <Box sx={styles.fileError}>{i18n.t('divFormService.fileStillUploading') as string}</Box>}
      {fileUploadError && <Box sx={styles.fileError}>{i18n.t('divFormService.fileError') as string}</Box>}
      
    </Box>
  </>);
  
};

const styles = {
  equipmentCard: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '40px 30px',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '36px',
    alignSelf: 'stretch',
    borderRadius: '12px',
    border: '1px solid var(--primary-hover, #ECEEF3)',
    boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.20), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
    marginBottom: '48px',
  },
  sectionTitleContainer: {
    display: 'flex',
    marginTop: 20,
  },
  fileError: {
    color: '#f44336'
  },
  submitButtons: {
    display: 'flex',
    justifyContent: 'flexEnd',
    padding: '20px 0',
  },
  submitButton: {
    display: 'flex',
    flexDirection: 'column'
  },
  datePicker: {
    maxWidth: '150px'
  },
}

export default Checklist;
